import { useNavigate } from "react-router-dom";
import { Sparkles, FolderArchive, MessagesSquare, ShoppingBasket, CalendarDays, ArrowRight, Copy, Check } from "lucide-react";
import { useSession } from "@/App";
import { useState } from "react";
import { toast } from "sonner";

const features = [
  { icon: FolderArchive, title: "Carica i tuoi documenti", text: "PDF, immagini, scontrini, referti. L'AI li classifica automaticamente." },
  { icon: Sparkles, title: "Chiedi alla tua AI", text: "Quando scade l'assicurazione? Qual era la dose di Marco? Risposte immediate." },
  { icon: MessagesSquare, title: "Chat famiglia integrata", text: "WhatsApp-style con AI che ricorda gli impegni dalle conversazioni." },
  { icon: ShoppingBasket, title: "Lista spesa condivisa", text: "Aggiunta automatica dai messaggi: 'compra il latte' diventa una voce." },
  { icon: CalendarDays, title: "Agenda della famiglia", text: "Eventi, scadenze e promemoria in un unico calendario condiviso." },
];

export default function OnboardingPage() {
  const { user, family } = useSession();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(family?.invite_code || "");
    setCopied(true);
    toast.success("Codice copiato!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-app text-primary-fg px-6 py-10 md:py-16">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-11 h-11 rounded-2xl bg-brand grid place-items-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-semibold text-lg">Family Brain</div>
            <div className="text-xs text-tertiary-fg uppercase tracking-widest mt-0.5">Onboarding</div>
          </div>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
          Ciao {user?.name?.split(" ")[0]}, <br />
          <span className="text-brand">benvenuto in famiglia.</span>
        </h1>
        <p className="text-secondary-fg mt-4 text-base leading-relaxed max-w-lg">
          Hai appena creato <span className="text-primary-fg font-medium">{family?.name}</span>.
          Ecco cosa potrai fare:
        </p>

        <div className="space-y-3 mt-8">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="bg-surface border border-subtle rounded-2xl p-4 flex gap-4 items-start animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="w-10 h-10 rounded-xl bg-elevated grid place-items-center shrink-0">
                <f.icon className="w-5 h-5 text-brand" strokeWidth={1.6} />
              </div>
              <div>
                <div className="font-display font-medium">{f.title}</div>
                <div className="text-sm text-secondary-fg leading-relaxed mt-1">{f.text}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-surface border border-subtle rounded-3xl p-6">
          <div className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold">Codice invito famiglia</div>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex-1 font-mono text-2xl font-semibold tracking-widest text-brand bg-elevated rounded-xl px-4 py-3 text-center">
              {family?.invite_code}
            </div>
            <button
              onClick={copyCode}
              data-testid="copy-invite-button"
              className="btn-secondary !px-4"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-tertiary-fg mt-3 leading-relaxed">
            Condividi questo codice con i tuoi familiari per farli accedere.
          </p>
        </div>

        <button
          data-testid="onboarding-finish-button"
          onClick={() => navigate("/app")}
          className="btn-primary w-full mt-8 flex items-center justify-center gap-2"
        >
          Inizia <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
